import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, Table, Badge, Spinner, Alert, Form } from 'react-bootstrap';
import Select from 'react-select';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import budgetService from '../../../services/budgetService';

echarts.use([FunnelChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const fmt = (n) => n == null ? '0' : Number(n).toLocaleString('es-EC');
const fmtC = (n) => n == null ? '$0' : `$${Number(n).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SEGMENT_COLORS = {
  EQUIPOS: '#00d97e', HOGAR: '#f5803e', POSTPAGO: '#6b5eae',
  PREPAGO: '#27bcfd', BRAND: '#e63757', CORPORATIVO: '#17a2b8', MARCA: '#e63757',
};

const LEVEL_LABELS = ['Segmento', 'Campaña', 'Grupo de Anuncios', 'Anuncio'];
const LEVEL_ICONS = ['▶', '  ▶', '    ▶', '      •'];
const LEVEL_BG = ['#f0f4f8', '#ffffff', '#fafafa', '#f8f8f8'];

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 36, fontSize: '0.82rem' }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  option: (base) => ({ ...base, fontSize: '0.82rem' }),
  multiValue: (base) => ({ ...base, fontSize: '0.78rem' }),
};

const MetricRow = ({ name, level, cost, impressions, clicks, views, metric, metricLabel, isExpanded, canExpand, onToggle }) => {
  const indent = level * 24;
  const levelLabel = LEVEL_LABELS[level] || '';
  return (
    <tr onClick={canExpand ? onToggle : undefined}
      style={{ cursor: canExpand ? 'pointer' : 'default', backgroundColor: LEVEL_BG[level], fontSize: level >= 2 ? '0.78rem' : '0.82rem' }}>
      <td style={{ paddingLeft: indent + 8 }}>
        <span className="text-muted me-1" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
          {canExpand ? (isExpanded ? '▼' : '▶') : '•'}
        </span>
        {level === 0 && <Badge bg="light" text="dark" className="me-1" style={{ borderLeft: `3px solid ${SEGMENT_COLORS[name] || '#6c757d'}`, fontSize: '0.7rem' }}>{levelLabel}</Badge>}
        {level > 0 && <span className="text-muted me-1" style={{ fontSize: '0.65rem' }}>[{levelLabel}]</span>}
        <span style={{ fontWeight: level === 0 ? 600 : 400 }}>{name}</span>
      </td>
      <td className="text-end">{fmtC(cost)}</td>
      <td className="text-end">{fmt(impressions)}</td>
      <td className="text-end">{fmt(clicks)}</td>
      <td className="text-end">{fmt(views)}</td>
      <td className="text-end">{fmt(metric)}</td>
      <td className="text-end">{metric > 0 ? fmtC(cost / metric) : '-'}</td>
    </tr>
  );
};

const DrillDownSection = ({ segments, campaigns, adGroups, ads, metricKey, metricLabel }) => {
  const [expandedSegs, setExpandedSegs] = useState({});
  const [expandedCamps, setExpandedCamps] = useState({});
  const [expandedAGs, setExpandedAGs] = useState({});

  const toggleSeg = (s) => setExpandedSegs(p => ({ ...p, [s]: !p[s] }));
  const toggleCamp = (k) => setExpandedCamps(p => ({ ...p, [k]: !p[k] }));
  const toggleAG = (k) => setExpandedAGs(p => ({ ...p, [k]: !p[k] }));

  const totals = useMemo(() => segments.reduce((a, s) => ({
    cost: a.cost + s.cost, impressions: a.impressions + s.impressions,
    clicks: a.clicks + s.clicks, views: a.views + s.views, metric: a.metric + s.metric,
  }), { cost: 0, impressions: 0, clicks: 0, views: 0, metric: 0 }), [segments]);

  return (
    <div style={{ maxHeight: '550px', overflowY: 'auto' }}>
      <Table size="sm" hover className="mb-0" style={{ fontSize: '0.82rem' }}>
        <thead className="sticky-top bg-white">
          <tr><th>Nombre</th><th className="text-end">Costo</th><th className="text-end">Impresiones</th><th className="text-end">Clics</th><th className="text-end">Views</th><th className="text-end">{metricLabel}</th><th className="text-end">{metricLabel === 'Leads' ? 'CPL' : metricLabel === 'Mensajes' ? 'CPW' : 'CP Landing'}</th></tr>
        </thead>
        <tbody>
          {segments.map(seg => {
            const segCamps = campaigns.filter(c => c.productType === seg.name);
            return (
              <React.Fragment key={seg.name}>
                <MetricRow name={seg.name} level={0} cost={seg.cost} impressions={seg.impressions} clicks={seg.clicks} views={seg.views} metric={seg.metric}
                  metricLabel={metricLabel} canExpand={segCamps.length > 0} isExpanded={expandedSegs[seg.name]} onToggle={() => toggleSeg(seg.name)} />
                {expandedSegs[seg.name] && segCamps.map(camp => {
                  const campAGs = adGroups.filter(ag => ag.campaignKey === camp.campaignKey);
                  return (
                    <React.Fragment key={camp.campaignKey}>
                      <MetricRow name={camp.campaignName} level={1} cost={camp.cost} impressions={camp.impressions} clicks={camp.clicks} views={camp.videoViews || 0} metric={camp[metricKey] || 0}
                        metricLabel={metricLabel} canExpand={campAGs.length > 0} isExpanded={expandedCamps[camp.campaignKey]} onToggle={() => toggleCamp(camp.campaignKey)} />
                      {expandedCamps[camp.campaignKey] && campAGs.map(ag => {
                        const agAds = ads.filter(a => a.adGroupKey === ag.adGroupKey);
                        return (
                          <React.Fragment key={ag.adGroupKey}>
                            <MetricRow name={ag.adGroupName} level={2} cost={ag.cost} impressions={ag.impressions} clicks={ag.clicks} views={ag.videoViews || 0} metric={ag[metricKey] || 0}
                              metricLabel={metricLabel} canExpand={agAds.length > 0} isExpanded={expandedAGs[ag.adGroupKey]} onToggle={() => toggleAG(ag.adGroupKey)} />
                            {expandedAGs[ag.adGroupKey] && agAds.map(a => (
                              <MetricRow key={a.adKey} name={a.adName} level={3} cost={a.cost} impressions={a.impressions} clicks={a.clicks} views={a.videoViews || 0} metric={a[metricKey] || 0}
                                metricLabel={metricLabel} canExpand={false} isExpanded={false} onToggle={() => {}} />
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot className="fw-bold bg-light">
          <tr>
            <td>Total</td><td className="text-end">{fmtC(totals.cost)}</td><td className="text-end">{fmt(totals.impressions)}</td>
            <td className="text-end">{fmt(totals.clicks)}</td><td className="text-end">{fmt(totals.views)}</td>
            <td className="text-end">{fmt(totals.metric)}</td>
            <td className="text-end">{totals.metric > 0 ? fmtC(totals.cost / totals.metric) : '-'}</td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

const aggregateBySegment = (campaigns, metricKey) => {
  const map = {};
  campaigns.forEach(c => {
    const seg = c.productType || 'OTRO';
    if (!map[seg]) map[seg] = { name: seg, cost: 0, impressions: 0, clicks: 0, views: 0, metric: 0 };
    map[seg].cost += c.cost || 0;
    map[seg].impressions += c.impressions || 0;
    map[seg].clicks += c.clicks || 0;
    map[seg].views += c.videoViews || 0;
    map[seg].metric += c[metricKey] || 0;
  });
  return Object.values(map).sort((a, b) => b.cost - a.cost);
};

const ClaroDigitalReportTab = ({ jitData, dateFrom: defaultDateFrom, dateTo: defaultDateTo }) => {
  const [digitalData, setDigitalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterSegmento, setFilterSegmento] = useState([]);
  const [filterPlataforma, setFilterPlataforma] = useState([]);
  const [filterCampana, setFilterCampana] = useState([]);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom || '');
  const [dateTo, setDateTo] = useState(defaultDateTo || '');

  useEffect(() => {
    if (defaultDateFrom) setDateFrom(defaultDateFrom);
    if (defaultDateTo) setDateTo(defaultDateTo);
  }, [defaultDateFrom, defaultDateTo]);

  const loadDigitalData = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const res = await budgetService.getClaroDashboardMetrics({ dateFrom, dateTo });
      if (res.success) setDigitalData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { if (dateFrom && dateTo) loadDigitalData(); }, [loadDigitalData, dateFrom, dateTo]);

  const allCampaigns = useMemo(() => digitalData?.campaigns || [], [digitalData]);
  const allAdGroups = useMemo(() => digitalData?.adGroups || [], [digitalData]);
  const allAds = useMemo(() => digitalData?.ads || [], [digitalData]);

  const segmentoOptions = useMemo(() => {
    const segs = new Set(allCampaigns.map(c => c.productType).filter(Boolean));
    return [...segs].sort().map(s => ({ value: s, label: s }));
  }, [allCampaigns]);

  const plataformaOptions = useMemo(() => [
    { value: 'META', label: 'Meta' }, { value: 'GOOGLE', label: 'Google' }, { value: 'TIKTOK', label: 'TikTok' }, { value: 'LINKEDIN', label: 'LinkedIn' }, { value: 'ILLUMIN', label: 'Illumin / CTV' },
  ], []);

  const campanaOptions = useMemo(() =>
    allCampaigns.map(c => ({ value: String(c.campaignKey), label: c.campaignName })),
  [allCampaigns]);

  const filtered = useMemo(() => {
    let c = [...allCampaigns];
    if (filterSegmento.length) { const s = new Set(filterSegmento.map(o => o.value)); c = c.filter(x => s.has(x.productType)); }
    if (filterPlataforma.length) { const p = new Set(filterPlataforma.map(o => o.value)); c = c.filter(x => p.has(x.platform)); }
    if (filterCampana.length) { const ids = new Set(filterCampana.map(o => o.value)); c = c.filter(x => ids.has(String(x.campaignKey))); }
    return c;
  }, [allCampaigns, filterSegmento, filterPlataforma, filterCampana]);

  const campLeads = useMemo(() => filtered.filter(c => c.campaignObjective === 'LEADS'), [filtered]);
  const campWhatsapp = useMemo(() => filtered.filter(c => c.campaignObjective === 'WHATSAPP'), [filtered]);
  const campTrafico = useMemo(() => filtered.filter(c => c.campaignObjective === 'TRAFICO'), [filtered]);

  const resumenBySegment = useMemo(() => {
    const map = {};
    filtered.forEach(c => {
      const seg = c.productType || 'OTRO';
      if (!map[seg]) map[seg] = { cost: 0, impressions: 0, clicks: 0, views: 0 };
      map[seg].cost += c.cost || 0; map[seg].impressions += c.impressions || 0;
      map[seg].clicks += c.clicks || 0; map[seg].views += c.videoViews || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].cost - a[1].cost).map(([seg, v]) => ({
      segmento: seg, ...v,
      cpm: v.impressions > 0 ? (v.cost / v.impressions) * 1000 : 0,
      cpc: v.clicks > 0 ? v.cost / v.clicks : 0,
    }));
  }, [filtered]);

  const totalResumen = useMemo(() => resumenBySegment.reduce((a, r) => ({
    cost: a.cost + r.cost, impressions: a.impressions + r.impressions,
    clicks: a.clicks + r.clicks, views: a.views + r.views,
  }), { cost: 0, impressions: 0, clicks: 0, views: 0 }), [resumenBySegment]);

  // Funnel descendente correcto:
  // Leads Brutos = todos los registros que entran al JIT/SAC
  // Total Leads = leads digitales reales de pauta (Meta+Google+TikTok) — referencia
  // No Contactados = los que no se pudieron contactar
  // Inician Proceso = contactados que avanzan (Acepta + Agendado)
  // Acepta = cierres exitosos
  const funnelOpt = useMemo(() => {
    const k = jitData?.kpis || {};
    const registrosJit = parseInt(k.totalRegistros) || 0;
    const contactados = parseInt(k.totalContactados) || 0;
    const noContactados = parseInt(k.totalNoContactados) || 0;
    const acepta = parseInt(k.totalAcepta) || 0;
    const agendado = parseInt(k.totalAgendado) || 0;
    const inicianProceso = acepta + agendado;

    const steps = [
      { name: 'Leads Brutos', val: registrosJit, color: '#B8D4E3', desc: 'Total registros JIT/SAC' },
      { name: 'Total Leads', val: contactados, color: '#6BAED6', desc: `Contactados (${registrosJit > 0 ? ((contactados / registrosJit) * 100).toFixed(1) : 0}% de registros)` },
      { name: 'No Contactados', val: noContactados, color: '#FFC107', desc: `${registrosJit > 0 ? ((noContactados / registrosJit) * 100).toFixed(1) : 0}% sin contactar` },
      { name: 'Inician Proceso', val: inicianProceso, color: '#2171B5', desc: `Acepta (${acepta}) + Agendado (${agendado})` },
      { name: 'Acepta', val: acepta, color: '#28a745', desc: `${contactados > 0 ? ((acepta / contactados) * 100).toFixed(1) : 0}% tasa de cierre` },
    ];

    const maxVal = Math.max(registrosJit, 1);
    const norm = (v) => Math.max(Math.round((v / maxVal) * 100), 3);
    return {
      tooltip: { trigger: 'item', formatter: (p) => { const s = steps[p.dataIndex]; return `<strong>${s.name}</strong><br/>${fmt(s.val)}<br/><small>${s.desc}</small>`; } },
      series: [{ type: 'funnel', left: '10%', width: '80%', gap: 4, sort: 'descending',
        label: { show: true, position: 'inside', fontSize: 12, formatter: (p) => `${p.name}\n${fmt(p.data?.realValue)}` },
        data: steps.map(s => ({ name: s.name, value: norm(s.val), realValue: s.val, itemStyle: { color: s.color } })),
      }],
    };
  }, [jitData]);

  if (loading) return <div className="text-center py-5"><Spinner /><p className="mt-2 text-muted">Cargando reporte digital...</p></div>;

  return (
    <div>
      {/* FILTERS WITH DATE RANGE */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <small className="text-muted d-block mb-1">Fecha Desde</small>
              <Form.Control type="date" size="sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </Col>
            <Col md={2}>
              <small className="text-muted d-block mb-1">Fecha Hasta</small>
              <Form.Control type="date" size="sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </Col>
            <Col md={2}>
              <small className="text-muted d-block mb-1">Segmento</small>
              <Select isMulti isSearchable placeholder="Todos" options={segmentoOptions} value={filterSegmento} onChange={v => setFilterSegmento(v || [])} styles={selectStyles} isClearable />
            </Col>
            <Col md={2}>
              <small className="text-muted d-block mb-1">Plataforma</small>
              <Select isMulti isSearchable placeholder="Todas" options={plataformaOptions} value={filterPlataforma} onChange={v => setFilterPlataforma(v || [])} styles={selectStyles} isClearable />
            </Col>
            <Col md={3}>
              <small className="text-muted d-block mb-1">Campaña</small>
              <Select isMulti isSearchable placeholder="Todas" options={campanaOptions} value={filterCampana} onChange={v => setFilterCampana(v || [])} styles={selectStyles} isClearable />
            </Col>
            <Col md={1} className="text-end">
              <Badge bg="primary" className="py-2">{filtered.length}</Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!digitalData ? (
        <Alert variant="info">Selecciona un rango de fechas para cargar datos digitales.</Alert>
      ) : (
        <>
          {/* RESUMEN GENERAL */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-0 py-2"><strong>Resumen General</strong></Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" hover className="mb-0" style={{ fontSize: '0.82rem' }}>
                <thead><tr><th>Segmento</th><th className="text-end">Costo</th><th className="text-end">Impresiones</th><th className="text-end">Clics</th><th className="text-end">Views</th><th className="text-end">CPM</th><th className="text-end">CPC</th></tr></thead>
                <tbody>
                  {resumenBySegment.map(r => (
                    <tr key={r.segmento}>
                      <td><Badge bg="light" text="dark" style={{ borderLeft: `3px solid ${SEGMENT_COLORS[r.segmento] || '#6c757d'}` }} className="px-2">{r.segmento}</Badge></td>
                      <td className="text-end">{fmtC(r.cost)}</td><td className="text-end">{fmt(r.impressions)}</td>
                      <td className="text-end">{fmt(r.clicks)}</td><td className="text-end">{fmt(r.views)}</td>
                      <td className="text-end">{fmtC(r.cpm)}</td><td className="text-end">{fmtC(r.cpc)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="fw-bold bg-light">
                  <tr>
                    <td>Total</td><td className="text-end">{fmtC(totalResumen.cost)}</td><td className="text-end">{fmt(totalResumen.impressions)}</td>
                    <td className="text-end">{fmt(totalResumen.clicks)}</td><td className="text-end">{fmt(totalResumen.views)}</td>
                    <td className="text-end">{totalResumen.impressions > 0 ? fmtC((totalResumen.cost / totalResumen.impressions) * 1000) : '-'}</td>
                    <td className="text-end">{totalResumen.clicks > 0 ? fmtC(totalResumen.cost / totalResumen.clicks) : '-'}</td>
                  </tr>
                </tfoot>
              </Table>
            </Card.Body>
          </Card>

          {/* DETALLE LEADS */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-0 py-2">
              <strong>Detalle Campañas Leads</strong>
              <Badge bg="success" className="ms-2">{campLeads.length} campañas</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <DrillDownSection segments={aggregateBySegment(campLeads, 'leads')} campaigns={campLeads} adGroups={allAdGroups} ads={allAds} metricKey="leads" metricLabel="Leads" />
            </Card.Body>
          </Card>

          {/* DETALLE WHATSAPP */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-0 py-2">
              <strong>Detalle Campañas Whatsapp</strong>
              <Badge bg="success" className="ms-2">{campWhatsapp.length} campañas</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <DrillDownSection segments={aggregateBySegment(campWhatsapp, 'leads')} campaigns={campWhatsapp} adGroups={allAdGroups} ads={allAds} metricKey="leads" metricLabel="Mensajes" />
            </Card.Body>
          </Card>

          {/* DETALLE TRAFICO */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-0 py-2">
              <strong>Detalle Campañas Tráfico</strong>
              <Badge bg="success" className="ms-2">{campTrafico.length} campañas</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <DrillDownSection segments={aggregateBySegment(campTrafico, 'linkClicks')} campaigns={campTrafico} adGroups={allAdGroups} ads={allAds} metricKey="linkClicks" metricLabel="Landing Page Views" />
            </Card.Body>
          </Card>

          {/* FUNNEL LEADS — cruza Objetivos + Digital + JIT */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-0 py-2">
              <strong>Funnel Leads</strong>
              <small className="text-muted ms-2">(Brutos = leads digitales, Total = registros JIT, Inician = contactados JIT, Acepta = cierres)</small>
            </Card.Header>
            <Card.Body>
              <ReactEChartsCore echarts={echarts} option={funnelOpt} style={{ height: '350px' }} />
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default ClaroDigitalReportTab;
